import { CheckboxField, FormDrawerContent, FormFetchDrawer, isSuccess, useToasts } from "@dgs/core";
import { useQueryClient } from "@tanstack/react-query";
import { FC, useCallback } from "react";
import { useTranslation } from "react-i18next";
import { EventCalendarSettingFormValues, eventCalendarSettingService } from "./eventCalendarSettingService";
import { calendarEventService } from "./events/calendarEventService";
import { WeekdaySelectField } from "./WeekdaySelectField";
import { YasumiProviderSelect } from "./YasumiProviderSelect";

interface Props {
	open: boolean;
	onClose: () => void;
	onUpdateSuccess?: () => void;
}

const useValidate = () => {
	const { t } = useTranslation();

	return (values: EventCalendarSettingFormValues) => {
		const errors: { [K in keyof EventCalendarSettingFormValues]?: any } = {};

		if (!values.startDayOfTheWeek) {
			errors.startDayOfTheWeek = t("Required field");
		}

		if (!values.yasumiProvider) {
			errors.yasumiProvider = t("Required field");
		}

		return errors;
	};
};

export const EditCalendarSettingDrawer: FC<Props> = ({ onUpdateSuccess, ...props }) => {
	const { t } = useTranslation();
	const validate = useValidate();
	const { showToast } = useToasts();
	const queryClient = useQueryClient();

	const update = useCallback(
		async (values: EventCalendarSettingFormValues) => {
			const response = await eventCalendarSettingService.update({
				...values,
				yasumiProviderId: values.yasumiProvider.id,
			});

			if (isSuccess(response)) {
				onUpdateSuccess && onUpdateSuccess();
				await queryClient.invalidateQueries({ queryKey: eventCalendarSettingService.showKey });
				await queryClient.invalidateQueries({ queryKey: calendarEventService.calendarKey });
				showToast({
					type: "success",
					title: t("Event calendar settings"),
					body: t("Saved successfully"),
				});
				return true;
			}

			return false;
		},
		[onUpdateSuccess, queryClient, showToast, t],
	);

	return (
		<FormFetchDrawer
			title={t("Event calendar settings")}
			validate={validate}
			queryFn={() => eventCalendarSettingService.show().then((x) => x.data.data)}
			queryKey={eventCalendarSettingService.showKey}
			renderSuccess={(props) => (
				<FormDrawerContent {...props}>
					<CheckboxField label={t("Show calendar weeks")} name="showCalendarWeeks" />
					<CheckboxField label={t("Show holidays")} name="showHolidays" />
					<YasumiProviderSelect label={t("Holiday region")} name="yasumiProvider" />
					<WeekdaySelectField label={t("Start day of the week")} name="startDayOfTheWeek" />
				</FormDrawerContent>
			)}
			onSubmit={update}
			open={props.open}
			onClose={props.onClose}
		/>
	);
};
